import {
  arrayTraversal,
  twoSum,
  slidingWindow,
  bubbleSort,
  selectionSort,
  mergeSort,
  quickSort,
  binarySearch,
  stackOps,
  queueOps,
  linkedListTraversal,
  containsDuplicate,
  bestTimeToBuySell,
  validPalindrome,
  majorityElement,
  firstBadVersion,
  climbingStairs,
  validParentheses,
  reverseLinkedList,
  addBinary,
  linkedListCycle,
  queueUsingStacks,
  validAnagram,
  ransomNote,
  longestPalindrome,
  middleLinkedList,
  mergeTwoSortedLists,
  longestSubstringNoRepeat,
  longestPalindromicSubstring,
  containerWithMostWater,
  threeSum,
  searchRotatedSortedArray,
  maximumSubarray,
  mergeIntervals,
  insertInterval,
  sortColors,
  evalRPN,
  minStack,
  productExceptSelf,
  findAllAnagrams,
  letterCombinations,
  taskScheduler,
} from './algorithms'
import { treeDFS, treeBFS, invertTree, validateBST, maxDepthTree, balancedTree, diameterTree, rightSideView, kthSmallestBST, lcaBST, lcaBinaryTree, constructBinaryTree } from './treeAlgorithms'
import { graphBFS, graphDFS, courseSchedule, cloneGraphProblem } from './graphAlgorithms'
import { floodFill, numberOfIslands, coinChange, uniquePaths, kClosestPoints, zeroOneMatrix, rottingOranges, wordSearch, spiralMatrix, wordBreak, partitionEqualSubsetSum } from './gridDPAlgorithms'

// Each entry describes one algorithm.
// getSteps() returns the precomputed step array.
// visualizer tells the scene which renderer to use (maps to RENDERER_MAP in DSAScene).

export const ALGORITHM_REGISTRY = {
  // ── Arrays ──────────────────────────────────────────────────────────────
  'array-traversal': {
    id: 'array-traversal',
    label: 'Array Traversal',
    category: 'Arrays',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Visit every element once, left to right.',
    getSteps: () => arrayTraversal([3, 1, 4, 1, 5, 9, 2, 6]),
  },
  'two-sum': {
    id: 'two-sum',
    label: 'Two Sum',
    category: 'Arrays',
    complexity: { time: 'O(n)', space: 'O(n)' },
    visualizer: 'array',
    description: 'Return indices of two numbers that add up to target.',
    getSteps: () => twoSum([2, 7, 11, 15, 3, 6], 9),
  },
  'sliding-window': {
    id: 'sliding-window',
    label: 'Sliding Window',
    category: 'Arrays',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Maximum subarray sum of size k.',
    getSteps: () => slidingWindow([2, 1, 5, 1, 3, 2], 3),
  },

  // ── Sorting ─────────────────────────────────────────────────────────────
  'bubble-sort': {
    id: 'bubble-sort',
    label: 'Bubble Sort',
    category: 'Sorting',
    complexity: { time: 'O(n²)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Repeatedly swap adjacent elements that are out of order.',
    getSteps: () => bubbleSort([5, 3, 8, 1, 9, 2, 7, 4]),
  },
  'selection-sort': {
    id: 'selection-sort',
    label: 'Selection Sort',
    category: 'Sorting',
    complexity: { time: 'O(n²)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Find the minimum, place it at the front each pass.',
    getSteps: () => selectionSort([5, 3, 8, 1, 9, 2, 7, 4]),
  },
  'merge-sort': {
    id: 'merge-sort',
    label: 'Merge Sort',
    category: 'Sorting',
    complexity: { time: 'O(n log n)', space: 'O(n)' },
    visualizer: 'array',
    description: 'Divide and conquer — split, sort halves, merge.',
    getSteps: () => mergeSort([5, 3, 8, 1, 9, 2, 7, 4]),
  },
  'quick-sort': {
    id: 'quick-sort',
    label: 'Quick Sort',
    category: 'Sorting',
    complexity: { time: 'O(n log n)', space: 'O(log n)' },
    visualizer: 'array',
    description: 'Partition around pivot, recursively sort partitions.',
    getSteps: () => quickSort([5, 3, 8, 1, 9, 2, 7, 4]),
  },

  // ── Search ──────────────────────────────────────────────────────────────
  'binary-search': {
    id: 'binary-search',
    label: 'Binary Search',
    category: 'Search',
    complexity: { time: 'O(log n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Halve the search space each step on a sorted array.',
    getSteps: () => binarySearch([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], 7),
  },

  // ── Stack ────────────────────────────────────────────────────────────────
  'stack': {
    id: 'stack',
    label: 'Stack (LIFO)',
    category: 'Stack & Queue',
    complexity: { time: 'O(1)', space: 'O(n)' },
    visualizer: 'stack',
    description: 'Push and pop — Last In, First Out.',
    getSteps: () =>
      stackOps([
        { op: 'push', val: 5 },
        { op: 'push', val: 3 },
        { op: 'push', val: 8 },
        { op: 'pop' },
        { op: 'push', val: 1 },
        { op: 'push', val: 9 },
        { op: 'pop' },
        { op: 'pop' },
      ]),
  },

  // ── Queue ────────────────────────────────────────────────────────────────
  'queue': {
    id: 'queue',
    label: 'Queue (FIFO)',
    category: 'Stack & Queue',
    complexity: { time: 'O(1)', space: 'O(n)' },
    visualizer: 'queue',
    description: 'Enqueue and dequeue — First In, First Out.',
    getSteps: () =>
      queueOps([
        { op: 'enqueue', val: 1 },
        { op: 'enqueue', val: 2 },
        { op: 'enqueue', val: 3 },
        { op: 'dequeue' },
        { op: 'enqueue', val: 4 },
        { op: 'enqueue', val: 5 },
        { op: 'dequeue' },
      ]),
  },

  // ── Linked List ──────────────────────────────────────────────────────────
  'linked-list': {
    id: 'linked-list',
    label: 'Linked List Traversal',
    category: 'Linked List',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Follow next pointers from head to null.',
    getSteps: () => linkedListTraversal([7, 14, 21, 28, 35, 42]),
  },

  // ── Grind 75 · Easy ───────────────────────────────────────────────────────
  'contains-duplicate': {
    id: 'contains-duplicate',
    label: 'Contains Duplicate',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(n)' },
    visualizer: 'array',
    description: 'Detect any repeated value using a hash set.',
    getSteps: () => containsDuplicate([1, 2, 3, 4, 2, 5]),
  },
  'best-time-stock': {
    id: 'best-time-stock',
    label: 'Best Time to Buy and Sell Stock',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Track the lowest price and the best profit in one pass.',
    getSteps: () => bestTimeToBuySell([7, 1, 5, 3, 6, 4]),
  },
  'valid-palindrome': {
    id: 'valid-palindrome',
    label: 'Valid Palindrome',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Two pointers compare characters from both ends.',
    getSteps: () => validPalindrome('racecar'),
  },
  'majority-element': {
    id: 'majority-element',
    label: 'Majority Element',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: "Boyer–Moore voting to find the > n/2 element.",
    getSteps: () => majorityElement([2, 2, 1, 1, 1, 2, 2]),
  },
  'first-bad-version': {
    id: 'first-bad-version',
    label: 'First Bad Version',
    category: 'Grind 75',
    complexity: { time: 'O(log n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Binary-search the first failing version.',
    getSteps: () => firstBadVersion(10, 6),
  },
  'climbing-stairs': {
    id: 'climbing-stairs',
    label: 'Climbing Stairs',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(n)' },
    visualizer: 'array',
    description: 'Fill a DP table: dp[i] = dp[i-1] + dp[i-2].',
    getSteps: () => climbingStairs(6),
  },
  'valid-parentheses': {
    id: 'valid-parentheses',
    label: 'Valid Parentheses',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(n)' },
    visualizer: 'stack',
    description: 'Match brackets using a stack.',
    getSteps: () => validParentheses('([{}])'),
  },
  'reverse-linked-list': {
    id: 'reverse-linked-list',
    label: 'Reverse Linked List',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'linked-list',
    description: 'Re-point each node to its predecessor.',
    getSteps: () => reverseLinkedList([1, 2, 3, 4, 5]),
  },
  'add-binary': {
    id: 'add-binary',
    label: 'Add Binary',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(n)' },
    visualizer: 'array',
    description: 'Add two binary strings digit-by-digit with carry.',
    getSteps: () => addBinary('1010', '1011'),
  },
  'linked-list-cycle': {
    id: 'linked-list-cycle',
    label: 'Linked List Cycle',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'linked-list',
    description: "Floyd's slow/fast pointer cycle detection.",
    getSteps: () => linkedListCycle([3, 2, 0, -4], 1),
  },
  'queue-using-stacks': {
    id: 'queue-using-stacks',
    label: 'Implement Queue using Stacks',
    category: 'Grind 75',
    complexity: { time: 'O(1) amortized', space: 'O(n)' },
    visualizer: 'stack',
    description: 'Simulate a FIFO queue with two LIFO stacks.',
    getSteps: () => queueUsingStacks(),
  },
  'valid-anagram': {
    id: 'valid-anagram',
    label: 'Valid Anagram',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Char-frequency map: all counts must be 0 after both strings.',
    getSteps: () => validAnagram('anagram', 'nagaram'),
  },
  'ransom-note': {
    id: 'ransom-note',
    label: 'Ransom Note',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Check if note chars can all be found in the magazine.',
    getSteps: () => ransomNote('aa', 'aab'),
  },
  'longest-palindrome': {
    id: 'longest-palindrome',
    label: 'Longest Palindrome',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Pair up even-count chars; one odd-count char sits in the center.',
    getSteps: () => longestPalindrome('abccccdd'),
  },
  'middle-linked-list': {
    id: 'middle-linked-list',
    label: 'Middle of Linked List',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'linked-list',
    description: 'Slow/fast pointer — when fast ends, slow is at the middle.',
    getSteps: () => middleLinkedList([1, 2, 3, 4, 5]),
  },

  // ── Grind 75 · Easy (new) ─────────────────────────────────────────────────────
  'merge-two-sorted-lists': {
    id: 'merge-two-sorted-lists',
    label: 'Merge Two Sorted Lists',
    category: 'Grind 75',
    complexity: { time: 'O(m+n)', space: 'O(1)' },
    visualizer: 'linked-list',
    description: 'Two pointers step through both lists, always picking the smaller value.',
    getSteps: () => mergeTwoSortedLists(),
  },

  // ── Grind 75 · Medium ─────────────────────────────────────────────────────────
  'longest-substring-no-repeat': {
    id: 'longest-substring-no-repeat',
    label: 'Longest Substring Without Repeating Chars',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(min(m,n))' },
    visualizer: 'array',
    description: 'Sliding window — shrink left on any duplicate, track the max length seen.',
    getSteps: () => longestSubstringNoRepeat(),
  },
  'longest-palindromic-substring': {
    id: 'longest-palindromic-substring',
    label: 'Longest Palindromic Substring',
    category: 'Grind 75',
    complexity: { time: 'O(n²)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Expand around each center (odd and even) to find the longest palindrome.',
    getSteps: () => longestPalindromicSubstring(),
  },
  'container-with-most-water': {
    id: 'container-with-most-water',
    label: 'Container With Most Water',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Two pointers from both ends; always move the shorter wall inward.',
    getSteps: () => containerWithMostWater(),
  },
  'three-sum': {
    id: 'three-sum',
    label: '3Sum',
    category: 'Grind 75',
    complexity: { time: 'O(n²)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Sort, fix one element, two-pointer on the rest to find zero-sum triplets.',
    getSteps: () => threeSum(),
  },
  'search-rotated-sorted-array': {
    id: 'search-rotated-sorted-array',
    label: 'Search in Rotated Sorted Array',
    category: 'Grind 75',
    complexity: { time: 'O(log n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Binary search — at each mid, one half is always sorted; decide which side to search.',
    getSteps: () => searchRotatedSortedArray(),
  },
  'maximum-subarray': {
    id: 'maximum-subarray',
    label: 'Maximum Subarray',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: "Kadane's algorithm — reset the running sum when it goes negative.",
    getSteps: () => maximumSubarray(),
  },
  'merge-intervals': {
    id: 'merge-intervals',
    label: 'Merge Intervals',
    category: 'Grind 75',
    complexity: { time: 'O(n log n)', space: 'O(n)' },
    visualizer: 'array',
    description: 'Sort by start; merge each interval into the last result if they overlap.',
    getSteps: () => mergeIntervals(),
  },
  'insert-interval': {
    id: 'insert-interval',
    label: 'Insert Interval',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(n)' },
    visualizer: 'array',
    description: 'Add all non-overlapping intervals before the new one, merge overlaps, append rest.',
    getSteps: () => insertInterval(),
  },
  'sort-colors': {
    id: 'sort-colors',
    label: 'Sort Colors',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Dutch National Flag — three pointers partition 0s, 1s, 2s in one pass.',
    getSteps: () => sortColors(),
  },
  'eval-rpn': {
    id: 'eval-rpn',
    label: 'Evaluate Reverse Polish Notation',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(n)' },
    visualizer: 'stack',
    description: 'Push numbers; on operator pop two, compute, push result.',
    getSteps: () => evalRPN(),
  },
  'min-stack': {
    id: 'min-stack',
    label: 'Min Stack',
    category: 'Grind 75',
    complexity: { time: 'O(1)', space: 'O(n)' },
    visualizer: 'stack',
    description: 'Parallel min-stack tracks the current minimum at each level.',
    getSteps: () => minStack(),
  },
  'product-except-self': {
    id: 'product-except-self',
    label: 'Product of Array Except Self',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Two passes: prefix products left-to-right, suffix products right-to-left.',
    getSteps: () => productExceptSelf(),
  },
  'find-all-anagrams': {
    id: 'find-all-anagrams',
    label: 'Find All Anagrams in a String',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Fixed-size sliding window comparing character frequency maps.',
    getSteps: () => findAllAnagrams(),
  },
  'letter-combinations': {
    id: 'letter-combinations',
    label: 'Letter Combinations of a Phone Number',
    category: 'Grind 75',
    complexity: { time: 'O(4ⁿ)', space: 'O(n)' },
    visualizer: 'array',
    description: 'DFS/backtracking — for each digit map its letters, recurse on remaining digits.',
    getSteps: () => letterCombinations(),
  },
  'task-scheduler': {
    id: 'task-scheduler',
    label: 'Task Scheduler',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(1)' },
    visualizer: 'array',
    description: 'Formula: (maxFreq-1)*(n+1)+countOfMaxFreq, or tasks.length — whichever is larger.',
    getSteps: () => taskScheduler(),
  },
  'clone-graph': {
    id: 'clone-graph',
    label: 'Clone Graph',
    category: 'Grind 75',
    complexity: { time: 'O(V+E)', space: 'O(V)' },
    visualizer: 'graph',
    description: 'BFS — for each node create a clone and connect it to cloned neighbors.',
    getSteps: () => cloneGraphProblem(),
  },
  'right-side-view': {
    id: 'right-side-view',
    label: 'Binary Tree Right Side View',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(n)' },
    visualizer: 'tree',
    description: 'BFS level-order; collect the last node at each level.',
    getSteps: () => rightSideView(),
  },
  'kth-smallest-bst': {
    id: 'kth-smallest-bst',
    label: 'Kth Smallest in BST',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(h)' },
    visualizer: 'tree',
    description: 'In-order DFS of BST gives ascending order — stop at the kth node.',
    getSteps: () => kthSmallestBST(),
  },
  'lca-bst': {
    id: 'lca-bst',
    label: 'Lowest Common Ancestor of BST',
    category: 'Grind 75',
    complexity: { time: 'O(h)', space: 'O(1)' },
    visualizer: 'tree',
    description: 'Walk down BST: if both values are on the same side, recurse; else current node is LCA.',
    getSteps: () => lcaBST(),
  },
  'lca-binary-tree': {
    id: 'lca-binary-tree',
    label: 'Lowest Common Ancestor of Binary Tree',
    category: 'Grind 75',
    complexity: { time: 'O(n)', space: 'O(h)' },
    visualizer: 'tree',
    description: 'Post-order DFS — node is LCA when both targets found in different subtrees.',
    getSteps: () => lcaBinaryTree(),
  },
  'construct-binary-tree': {
    id: 'construct-binary-tree',
    label: 'Construct Binary Tree from Preorder/Inorder',
    category: 'Grind 75',
    complexity: { time: 'O(n²)', space: 'O(n)' },
    visualizer: 'tree',
    description: 'Preorder[0] is root; find it in inorder to split left/right subtrees.',
    getSteps: () => constructBinaryTree(),
  },
  'zero-one-matrix': {
    id: 'zero-one-matrix',
    label: '01 Matrix',
    category: 'Grind 75',
    complexity: { time: 'O(m·n)', space: 'O(m·n)' },
    visualizer: 'grid',
    description: 'Multi-source BFS from all 0-cells simultaneously — distance propagates outward.',
    getSteps: () => zeroOneMatrix(),
  },
  'rotting-oranges': {
    id: 'rotting-oranges',
    label: 'Rotting Oranges',
    category: 'Grind 75',
    complexity: { time: 'O(m·n)', space: 'O(m·n)' },
    visualizer: 'grid',
    description: 'Multi-source BFS from all rotten oranges; each minute rot spreads to neighbors.',
    getSteps: () => rottingOranges(),
  },
  'word-search': {
    id: 'word-search',
    label: 'Word Search',
    category: 'Grind 75',
    complexity: { time: 'O(m·n·4^L)', space: 'O(L)' },
    visualizer: 'grid',
    description: 'DFS backtracking — explore 4 directions, mark visited, unmark on backtrack.',
    getSteps: () => wordSearch(),
  },
  'spiral-matrix': {
    id: 'spiral-matrix',
    label: 'Spiral Matrix',
    category: 'Grind 75',
    complexity: { time: 'O(m·n)', space: 'O(1)' },
    visualizer: 'grid',
    description: 'Shrink boundaries layer by layer: right → down → left → up.',
    getSteps: () => spiralMatrix(),
  },
  'word-break': {
    id: 'word-break',
    label: 'Word Break',
    category: 'Grind 75',
    complexity: { time: 'O(n²)', space: 'O(n)' },
    visualizer: 'dp-table',
    description: 'dp[i] = true if s[0..i-1] can be segmented; check all words ending at i.',
    getSteps: () => wordBreak(),
  },
  'partition-equal-subset-sum': {
    id: 'partition-equal-subset-sum',
    label: 'Partition Equal Subset Sum',
    category: 'Grind 75',
    complexity: { time: 'O(n·sum)', space: 'O(n·sum)' },
    visualizer: 'dp-table',
    description: '0/1 knapsack DP — can we pick a subset summing to total/2?',
    getSteps: () => partitionEqualSubsetSum(),
  },

  // ── Trees ────────────────────────────────────────────────────────────────────
  'tree-dfs': {
    id: 'tree-dfs',
    label: 'Tree DFS (Pre-order)',
    category: 'Trees',
    complexity: { time: 'O(n)', space: 'O(h)' },
    visualizer: 'tree',
    description: 'Depth-First Search — visit node, then left subtree, then right subtree.',
    getSteps: () => treeDFS(),
  },
  'tree-bfs': {
    id: 'tree-bfs',
    label: 'Tree BFS (Level-order)',
    category: 'Trees',
    complexity: { time: 'O(n)', space: 'O(n)' },
    visualizer: 'tree',
    description: 'Level-order traversal — process all nodes at each depth using a queue.',
    getSteps: () => treeBFS(),
  },
  'invert-tree': {
    id: 'invert-tree',
    label: 'Invert Binary Tree',
    category: 'Trees',
    complexity: { time: 'O(n)', space: 'O(n)' },
    visualizer: 'tree',
    description: 'Mirror a binary tree by swapping left and right children at every node.',
    getSteps: () => invertTree(),
  },
  'validate-bst': {
    id: 'validate-bst',
    label: 'Validate BST',
    category: 'Trees',
    complexity: { time: 'O(n)', space: 'O(h)' },
    visualizer: 'tree',
    description: 'Verify a BST via in-order traversal — values must be strictly increasing.',
    getSteps: () => validateBST(),
  },
  'max-depth-tree': {
    id: 'max-depth-tree',
    label: 'Maximum Depth of Binary Tree',
    category: 'Trees',
    complexity: { time: 'O(n)', space: 'O(h)' },
    visualizer: 'tree',
    description: 'DFS returns height at each node; take the max of left/right + 1.',
    getSteps: () => maxDepthTree(),
  },
  'balanced-tree': {
    id: 'balanced-tree',
    label: 'Balanced Binary Tree',
    category: 'Trees',
    complexity: { time: 'O(n)', space: 'O(h)' },
    visualizer: 'tree',
    description: 'DFS checks height difference ≤ 1 at every node.',
    getSteps: () => balancedTree(),
  },
  'diameter-tree': {
    id: 'diameter-tree',
    label: 'Diameter of Binary Tree',
    category: 'Trees',
    complexity: { time: 'O(n)', space: 'O(h)' },
    visualizer: 'tree',
    description: 'At each node: diameter = leftDepth + rightDepth. Track the global max.',
    getSteps: () => diameterTree(),
  },

  // ── Graphs ───────────────────────────────────────────────────────────────────
  'graph-bfs': {
    id: 'graph-bfs',
    label: 'Graph BFS',
    category: 'Graphs',
    complexity: { time: 'O(V+E)', space: 'O(V)' },
    visualizer: 'graph',
    description: 'Explore neighbours level by level from a start node using a queue.',
    getSteps: () => graphBFS(),
  },
  'graph-dfs': {
    id: 'graph-dfs',
    label: 'Graph DFS',
    category: 'Graphs',
    complexity: { time: 'O(V+E)', space: 'O(V)' },
    visualizer: 'graph',
    description: 'Follow each path as deep as possible before backtracking.',
    getSteps: () => graphDFS(),
  },
  'course-schedule': {
    id: 'course-schedule',
    label: 'Course Schedule',
    category: 'Graphs',
    complexity: { time: 'O(V+E)', space: 'O(V)' },
    visualizer: 'graph',
    description: "Kahn's topological sort — detect if a course dependency graph has a cycle.",
    getSteps: () => courseSchedule(),
  },

  // ── Grid ─────────────────────────────────────────────────────────────────────
  'flood-fill': {
    id: 'flood-fill',
    label: 'Flood Fill',
    category: 'Grid',
    complexity: { time: 'O(m·n)', space: 'O(m·n)' },
    visualizer: 'grid',
    description: 'DFS from a starting cell, recoloring all 4-connected same-color neighbors.',
    getSteps: () => floodFill(),
  },
  'number-of-islands': {
    id: 'number-of-islands',
    label: 'Number of Islands',
    category: 'Grid',
    complexity: { time: 'O(m·n)', space: 'O(m·n)' },
    visualizer: 'grid',
    description: 'Count connected "1" groups via DFS — sink each island as it is found.',
    getSteps: () => numberOfIslands(),
  },

  // ── DP Table ─────────────────────────────────────────────────────────────────
  'coin-change': {
    id: 'coin-change',
    label: 'Coin Change',
    category: 'DP',
    complexity: { time: 'O(amount·n)', space: 'O(amount)' },
    visualizer: 'dp-table',
    description: 'Bottom-up DP: dp[i] = min coins to make amount i.',
    getSteps: () => coinChange(),
  },
  'unique-paths': {
    id: 'unique-paths',
    label: 'Unique Paths',
    category: 'DP',
    complexity: { time: 'O(m·n)', space: 'O(m·n)' },
    visualizer: 'dp-table',
    description: '2D DP: dp[r][c] = dp[r-1][c] + dp[r][c-1] — paths from top-left to bottom-right.',
    getSteps: () => uniquePaths(),
  },

  // ── Heap ─────────────────────────────────────────────────────────────────────
  'k-closest-points': {
    id: 'k-closest-points',
    label: 'K Closest Points to Origin',
    category: 'Heap',
    complexity: { time: 'O(n log k)', space: 'O(k)' },
    visualizer: 'heap',
    description: 'Max-heap of size k: eject the farthest point whenever size exceeds k.',
    getSteps: () => kClosestPoints(),
  },
}

export function getAlgorithmsByCategory(category) {
  return Object.values(ALGORITHM_REGISTRY).filter((a) => a.category === category)
}

// Derived from registry — single source of truth for category list.
export function getCategories() {
  return [...new Set(Object.values(ALGORITHM_REGISTRY).map((a) => a.category))]
}
